import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';

const DemoWalletContext = createContext(null);

const DEMO_KEY = 'palment_demo_wallet_v1';
// A deterministic-looking fake Solana address for demo mode — never signs anything.
const DEMO_ADDRESS = '7pALMeNtDem0yPUSDvauLT9aBcDeFgHiJkLmN0pQrStU';

function readDemoFlag() {
  try {
    return localStorage.getItem(DEMO_KEY) === '1';
  } catch (err) {
    console.warn('[palment] failed to read demo wallet flag', err);
    return false;
  }
}

function writeDemoFlag(active) {
  try {
    localStorage.setItem(DEMO_KEY, active ? '1' : '0');
  } catch (err) {
    console.warn('[palment] failed to persist demo wallet flag', err);
  }
}

export function DemoWalletProvider({ children }) {
  const { publicKey: realKey, connected: realConnected, disconnect: realDisconnect } = useWallet();
  const { setVisible } = useWalletModal();

  // Hydrate from storage synchronously — avoids the extra initial render.
  const [demoActive, setDemoActive] = useState(readDemoFlag);

  useEffect(() => {
    writeDemoFlag(demoActive);
  }, [demoActive]);

  // Turning on real wallet disables demo mode automatically.
  useEffect(() => {
    if (realConnected) setDemoActive(false);
  }, [realConnected]);

  const startDemo = useCallback(() => setDemoActive(true), []);
  const stopDemo = useCallback(() => setDemoActive(false), []);

  const openRealWallet = useCallback(() => {
    setDemoActive(false);
    setVisible(true);
  }, [setVisible]);

  const disconnectAll = useCallback(() => {
    setDemoActive(false);
    if (realConnected) realDisconnect();
  }, [realConnected, realDisconnect]);

  const address = realConnected && realKey ? realKey.toBase58() : demoActive ? DEMO_ADDRESS : null;
  const connected = realConnected || demoActive;
  const mode = realConnected ? 'real' : demoActive ? 'demo' : 'disconnected';

  const value = useMemo(
    () => ({ connected, address, mode, startDemo, stopDemo, openRealWallet, disconnectAll, demoActive }),
    [connected, address, mode, startDemo, stopDemo, openRealWallet, disconnectAll, demoActive]
  );

  return <DemoWalletContext.Provider value={value}>{children}</DemoWalletContext.Provider>;
}

export function usePalmentWallet() {
  const ctx = useContext(DemoWalletContext);
  if (!ctx) throw new Error('usePalmentWallet must be used inside DemoWalletProvider');
  return ctx;
}
